import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Plus, Download, Bell, Trophy, Gem, ChevronDown,
  TrendingDown, ArrowUpDown, Edit, Trash2, Award, Users, MessageSquare
} from "lucide-react";

/*
  Eclectic Score / All Rounds redesign — preview-only.
  Faithful interpretation of the reference mockup (mobile-first horizontal scorecard,
  rich stat tiles, sticky Enter score CTA).
  Self-contained, no API calls. Static demo data.
*/

// --- Demo data ---------------------------------------------------------------
const COMP = {
  name: "Spring Eclectic 2026",
  rounds_played: 4,
  total_rounds: 10,
  end_date_label: "30 May 2026",
  course: "Royal Cinque Golf Club",
  tee: "White Tees",
  par_total: 72,
};

// Front 9, Back 9 par + best-eclectic scores
const PAR_OUT  = [4, 5, 3, 4, 4, 3, 4, 5, 4]; // 36
const PAR_IN   = [4, 3, 4, 5, 4, 5, 3, 4, 4]; // 36
const BEST_OUT = [4, 4, 3, 3, 4, 3, 3, 5, 4]; // 33
const BEST_IN  = [4, 2, 4, 5, 4, 5, 3, 4, 4]; // 35  (2 on hole 11 = Excellent)

// All rounds demo
const ROUNDS = [
  { round_number: 4, round_date: "2026-05-04", course_handicap: 14, scores_out: [5, 5, 3, 4, 4, 4, 4, 6, 5], scores_in: [4, 3, 4, 6, 4, 5, 3, 5, 5] },
  { round_number: 3, round_date: "2026-04-28", course_handicap: 14, scores_out: [4, 4, 3, 3, 4, 3, 4, 5, 4], scores_in: [5, 2, 4, 5, 5, 6, 4, 4, 5] },
  { round_number: 2, round_date: "2026-04-15", course_handicap: 15, scores_out: [4, 5, 4, 4, 4, 4, 3, 5, 4], scores_in: [4, 3, 5, 5, 4, 5, 3, 4, 4] },
  { round_number: 1, round_date: "2026-04-01", course_handicap: 16, scores_out: [5, 5, 3, 5, 5, 4, 5, 6, 4], scores_in: [4, 4, 4, 5, 4, 5, 4, 4, 4] },
];

// --- Bits ---------------------------------------------------------------------

function CompetitionHeader() {
  return (
    <header className="bg-surface border-b border-line">
      <div className="max-w-5xl mx-auto px-4 py-3 sm:px-8 sm:py-4 flex items-center justify-between gap-3">
        <Link to="/dashboard" className="inline-flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-ink flex items-center justify-center">
            <span className="text-white font-mono font-semibold text-sm">G</span>
          </div>
          <span className="text-lg font-semibold tracking-tight text-ink">Eclectic</span>
        </Link>
        <div className="flex items-center gap-2.5">
          <button className="w-9 h-9 rounded-full border border-line bg-surface text-ink-muted hover:text-ink hover:bg-app transition-colors flex items-center justify-center">
            <Bell className="w-4 h-4" />
          </button>
          <div className="w-9 h-9 rounded-full bg-accent-tint text-accent-deep flex items-center justify-center font-semibold text-sm">A</div>
        </div>
      </div>
    </header>
  );
}

function CompTitleBlock() {
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-8 pt-6 sm:pt-8">
      <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted mb-2">{COMP.name}</div>
      <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-ink leading-[1.05] mb-2">Your competition</h1>
      <p className="text-[14px] sm:text-[15px] text-ink-muted">
        {COMP.rounds_played} of {COMP.total_rounds} rounds played
        <span className="mx-2 text-ink-soft">·</span>
        ends {COMP.end_date_label}
      </p>
      <div className="flex flex-wrap items-center gap-2.5 mt-5">
        <button className="ge-btn-outline">
          <Download className="w-4 h-4" />
          <span>Download</span>
        </button>
      </div>
    </section>
  );
}

function StatCard({ label, value, valueAccent, sub, subIcon: SubIcon, subTone, testid }) {
  return (
    <div className="bg-surface px-4 py-4 sm:px-5 sm:py-5" data-testid={testid}>
      <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted">{label}</div>
      <div className={`font-mono tabular-nums text-[34px] sm:text-[38px] font-semibold tracking-tight leading-none mt-2 ${valueAccent || "text-ink"}`}>
        {value}
      </div>
      {sub && (
        <div className={`text-[12px] mt-2.5 inline-flex items-center gap-1.5 ${subTone || "text-ink-muted"}`}>
          {SubIcon ? <SubIcon className="w-3 h-3" /> : null}
          <span>{sub}</span>
        </div>
      )}
    </div>
  );
}

function StatGrid() {
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-8 mt-6 sm:mt-8">
      <div className="grid grid-cols-2 gap-px bg-line border border-line rounded-2xl overflow-hidden">
        <StatCard
          testid="stat-eclectic-net"
          label="Eclectic Net"
          value="62"
          valueAccent="text-accent"
          sub="3 since Round 3"
          subIcon={TrendingDown}
          subTone="text-accent"
        />
        <StatCard
          testid="stat-position"
          label="Position"
          value={<><span>4</span><span className="text-ink-soft text-[24px] ml-0.5">/23</span></>}
          sub="2 strokes from 3rd"
        />
        <StatCard
          testid="stat-rounds"
          label="Rounds"
          value={<><span>4</span><span className="text-ink-soft text-[24px] ml-0.5">/10</span></>}
          sub="6 left this season"
        />
        <StatCard
          testid="stat-best-hole"
          label="Best hole"
          value={<><span>2</span><span className="text-ink-muted text-[20px] ml-1.5 font-medium">on 11</span></>}
          sub="Excellent 2 · 28 Apr"
          subIcon={Gem}
          subTone="text-warm"
        />
      </div>
    </section>
  );
}

function ScoreTabs({ active, onChange }) {
  const tabs = [
    { id: "best", label: "Eclectic Score" },
    { id: "rounds", label: "All Rounds" },
    { id: "leaderboard", label: "Leaderboard" },
    { id: "social", label: "Social" },
  ];
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 mt-6 sm:mt-8">
      <div className="flex gap-1.5 p-1 bg-black/[0.04] rounded-[12px] overflow-x-auto -mx-1 scrollbar-hide">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            data-testid={`scorecard-tab-${t.id}`}
            className={`px-4 py-2.5 rounded-lg text-[13.5px] font-medium whitespace-nowrap transition-all ${
              active === t.id
                ? "bg-surface text-ink shadow-sm ring-1 ring-line"
                : "bg-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Horizontal scorecard -----------------------------------------------------
function ScorecardRow({ label, values, totalLabel, totalValue, valueRender, isTotalBold }) {
  return (
    <div className="grid grid-cols-[64px_repeat(9,minmax(36px,1fr))_56px_repeat(9,minmax(36px,1fr))_56px] items-center text-center">
      <div className="text-left text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted px-3 py-2.5">{label}</div>
      {values.slice(0, 9).map((v, i) => (
        <div key={`a${i}`} className="py-2.5">{valueRender ? valueRender(v, i) : v}</div>
      ))}
      <div className={`py-2.5 bg-app text-ink ${isTotalBold ? "font-semibold" : ""}`}>{totalValue.out}</div>
      {values.slice(9, 18).map((v, i) => (
        <div key={`b${i}`} className="py-2.5">{valueRender ? valueRender(v, i + 9) : v}</div>
      ))}
      <div className={`py-2.5 bg-app text-ink ${isTotalBold ? "font-semibold" : ""}`}>{totalValue.in}</div>
    </div>
  );
}

function ScorecardHeaderRow() {
  return (
    <div className="grid grid-cols-[64px_repeat(9,minmax(36px,1fr))_56px_repeat(9,minmax(36px,1fr))_56px] items-center text-center text-[11px] font-semibold tracking-[0.06em] uppercase text-ink-muted bg-app/60">
      <div className="text-left px-3 py-2.5">Hole</div>
      {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => (
        <div key={n} className="py-2.5 font-mono tabular-nums">{n}</div>
      ))}
      <div className="py-2.5 text-ink font-bold">Out</div>
      {Array.from({ length: 9 }, (_, i) => i + 10).map((n) => (
        <div key={n} className="py-2.5 font-mono tabular-nums">{n}</div>
      ))}
      <div className="py-2.5 text-ink font-bold">In</div>
    </div>
  );
}

function HorizontalScorecard({ par, best }) {
  const out = par.slice(0, 9).reduce((s, v) => s + v, 0);
  const inHalf = par.slice(9).reduce((s, v) => s + v, 0);
  const bestOut = best.slice(0, 9).reduce((s, v) => s + v, 0);
  const bestIn = best.slice(9).reduce((s, v) => s + v, 0);

  return (
    <div className="overflow-x-auto -mx-1 scrollbar-hide">
      <div className="min-w-[820px] sm:min-w-0">
        <ScorecardHeaderRow />
        <div className="border-t border-line">
          <ScorecardRow
            label="Par"
            values={par}
            totalValue={{ out, in: inHalf }}
            valueRender={(v) => (
              <span className="font-mono tabular-nums text-[15px] text-ink-muted">{v}</span>
            )}
          />
        </div>
        <div className="border-t border-line border-dashed">
          <ScorecardRow
            label="Best"
            values={best}
            totalValue={{ out: bestOut, in: bestIn }}
            isTotalBold
            valueRender={(v, i) => {
              const p = par[i];
              const under = v < p;
              const equal = v === p;
              return (
                <div className="relative inline-flex flex-col items-center">
                  <span className={`font-mono tabular-nums text-[16px] font-semibold ${
                    under ? "text-accent" : equal ? "text-ink" : "text-ink-soft"
                  }`}>{v}</span>
                  {under && <span className="block w-1 h-1 rounded-full bg-accent mt-0.5"></span>}
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}

// --- Eclectic Score tab ------------------------------------------------------
function EclecticScoreTab() {
  const par = [...PAR_OUT, ...PAR_IN];
  const best = [...BEST_OUT, ...BEST_IN];
  const totalPar = par.reduce((s, v) => s + v, 0);
  const totalBest = best.reduce((s, v) => s + v, 0);
  const diff = totalBest - totalPar;
  const net = totalBest - 7; // demo handicap halve
  const netDiff = net - totalPar;

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-8 mt-5 pb-32">
      {/* Score colour legend */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] px-1">
        <span className="font-semibold tracking-[0.06em] uppercase text-ink-soft text-[10.5px]">Score key</span>
        <span className="inline-flex items-center gap-1.5 text-ink-muted">
          <span className="font-mono tabular-nums text-accent font-semibold">3</span>
          <span className="w-1 h-1 rounded-full bg-accent inline-block"></span>
          <span>Under par</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-ink-muted">
          <span className="font-mono tabular-nums text-ink font-semibold">4</span>
          <span>Par</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-ink-muted">
          <span className="font-mono tabular-nums text-ink-soft font-semibold">5</span>
          <span>Over par</span>
        </span>
      </div>

      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-5 border-b border-line">
          <div>
            <h2 className="text-[18px] sm:text-xl font-semibold tracking-tight text-ink leading-tight">Your perfect round</h2>
            <p className="text-[12.5px] text-ink-muted mt-0.5">{COMP.course} · {COMP.tee} · par {totalPar}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center font-mono tabular-nums text-[12.5px] font-semibold px-2.5 py-1.5 rounded-full bg-accent-tint text-accent-deep border border-accent/20">
              {net} net · {netDiff < 0 ? netDiff : `+${netDiff}`}
            </span>
            <button className="ge-btn-outline text-[12.5px] py-1.5">PDF</button>
          </div>
        </div>

        <div className="p-1 sm:p-2">
          <HorizontalScorecard par={par} best={best} />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 sm:px-5 py-3 border-t border-line bg-app/40 text-[12px]">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-accent inline-block"></span>
            <span className="text-ink-muted">Best below par</span>
          </span>
          <span className="text-ink-muted">
            {diff < 0 ? `${diff} to par` : `+${diff} to par`}
            <span className="mx-2 text-ink-soft">·</span>
            gross <span className="text-ink font-mono tabular-nums font-semibold">{totalBest}</span>
            <span className="mx-2 text-ink-soft">·</span>
            net <span className="text-ink font-mono tabular-nums font-semibold">{net}</span>
          </span>
        </div>
      </div>

      {/* Improvement timeline placeholder */}
      <div className="mt-6 bg-surface border border-line rounded-2xl">
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-line">
          <h3 className="text-[15px] font-semibold tracking-tight text-ink">Improvement timeline</h3>
          <span className="text-[12px] text-ink-muted">{COMP.rounds_played} rounds played</span>
        </div>
        <div className="px-4 sm:px-5 py-4 space-y-3">
          {[
            { r: 4, d: "4 May", desc: "no holes improved", strokes: 0 },
            { r: 3, d: "28 Apr", desc: "improved 11 (3 → 2)", strokes: 1 },
            { r: 2, d: "15 Apr", desc: "improved 7, 16", strokes: 2 },
            { r: 1, d: "1 Apr", desc: "baseline round", strokes: null },
          ].map((row, i) => (
            <div key={i} className="grid grid-cols-[40px_1fr_auto] items-center gap-3">
              <div className="font-mono tabular-nums text-[13px] text-ink-muted">R{row.r}</div>
              <div>
                <div className="text-[13.5px] text-ink">{row.desc}</div>
                <div className="text-[11px] text-ink-soft">{row.d}</div>
              </div>
              <div className={`font-mono tabular-nums text-[13px] font-semibold ${
                row.strokes === null ? "text-ink-soft" : row.strokes > 0 ? "text-accent" : "text-ink-muted"
              }`}>
                {row.strokes === null ? "—" : row.strokes > 0 ? `−${row.strokes}` : "0"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- All Rounds tab ----------------------------------------------------------
function AllRoundsTab() {
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-8 mt-5 pb-32">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[18px] sm:text-xl font-semibold tracking-tight text-ink leading-tight">All rounds</h2>
          <p className="text-[12.5px] text-ink-muted mt-0.5">{ROUNDS.length} of {COMP.total_rounds} played</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="ge-btn-outline text-[12.5px] py-1.5">
            <ArrowUpDown className="w-3.5 h-3.5" />
            <span>Newest</span>
          </button>
          <button className="ge-btn-outline text-[12.5px] py-1.5">
            <Download className="w-3.5 h-3.5" />
            <span>Excel</span>
          </button>
        </div>
      </div>

      {/* Score colour legend */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] px-1">
        <span className="font-semibold tracking-[0.06em] uppercase text-ink-soft text-[10.5px]">Score key</span>
        <span className="inline-flex items-center gap-1.5 text-ink-muted">
          <span className="font-mono tabular-nums text-accent font-semibold">3</span>
          <span className="w-1 h-1 rounded-full bg-accent inline-block"></span>
          <span>Under par</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-ink-muted">
          <span className="font-mono tabular-nums text-ink font-semibold">4</span>
          <span>Par</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-ink-muted">
          <span className="font-mono tabular-nums text-ink-soft font-semibold">5</span>
          <span>Over par</span>
        </span>
      </div>

      <div className="space-y-3">
        {ROUNDS.map((r) => {
          const all = [...r.scores_out, ...r.scores_in];
          const par = [...PAR_OUT, ...PAR_IN];
          const total = all.reduce((s, v) => s + v, 0);
          const totalPar = par.reduce((s, v) => s + v, 0);
          const diff = total - totalPar;
          const diffStr = diff === 0 ? "E" : diff > 0 ? `+${diff}` : `${diff}`;
          const net = total - Math.round(r.course_handicap / 2);

          return (
            <div
              key={r.round_number}
              data-testid={`round-card-${r.round_number}`}
              className="bg-surface border border-line rounded-2xl overflow-hidden"
            >
              <div className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 sm:px-5 py-4 border-b border-line">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-ink text-white flex items-center justify-center">
                    <span className="font-mono tabular-nums text-base font-semibold">{r.round_number}</span>
                  </div>
                  <div>
                    <div className="text-[14.5px] font-semibold text-ink leading-tight">Round {r.round_number}</div>
                    <div className="text-[12px] text-ink-muted mt-0.5">
                      {new Date(r.round_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      <span className="mx-1.5 text-ink-soft">·</span>
                      h/cap <span className="font-mono tabular-nums">{r.course_handicap}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 sm:gap-5 text-right">
                  <div>
                    <div className="text-[10px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Gross</div>
                    <div className="font-mono tabular-nums text-[20px] font-semibold text-ink leading-tight mt-0.5">{total}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Net</div>
                    <div className="font-mono tabular-nums text-[20px] font-semibold text-ink leading-tight mt-0.5">{net}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Vs par</div>
                    <div className={`font-mono tabular-nums text-[20px] font-semibold leading-tight mt-0.5 ${
                      diff < 0 ? "text-accent" : diff > 0 ? "text-ink-muted" : "text-ink"
                    }`}>{diffStr}</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 ml-2">
                    <button className="w-8 h-8 rounded-lg border border-line text-ink-muted hover:text-ink hover:border-line-strong transition-colors flex items-center justify-center">
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button className="w-8 h-8 rounded-lg border border-line text-ink-muted hover:text-red hover:border-red/40 transition-colors flex items-center justify-center">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="px-1 sm:px-2 py-1 sm:py-2">
                <HorizontalScorecard par={par} best={all} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// --- Stub tabs ---------------------------------------------------------------
function PlaceholderTab({ icon: Icon, title }) {
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-8 mt-5 pb-32">
      <div className="bg-surface border border-line rounded-2xl p-10 text-center">
        <Icon className="w-10 h-10 text-ink-soft mx-auto mb-4" />
        <h3 className="text-[15px] font-semibold tracking-tight text-ink">{title}</h3>
        <p className="text-[12.5px] text-ink-muted mt-1">This tab uses the existing screen and is unchanged in this preview.</p>
      </div>
    </section>
  );
}

// --- Sticky CTA ---------------------------------------------------------------
function StickyEnterScoreCTA() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 pointer-events-none">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-5 flex justify-end">
        <button
          type="button"
          data-testid="sticky-enter-score"
          className="pointer-events-auto inline-flex items-center gap-2 bg-ink text-white font-semibold pl-4 pr-5 py-3.5 rounded-full shadow-lg shadow-ink/15 hover:bg-ink-hover transition-all"
        >
          <Plus className="w-5 h-5" />
          <span>Enter score</span>
        </button>
      </div>
    </div>
  );
}

// --- Page --------------------------------------------------------------------
export default function ScorecardMockupPreview() {
  const [tab, setTab] = useState("best");

  return (
    <div className="min-h-screen bg-app">
      <div className="bg-warm-tint/40 border-b border-warm/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-2 flex items-center justify-between gap-3">
          <div className="text-[11.5px] font-semibold tracking-[0.06em] uppercase text-warm flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" />
            Preview · scorecard redesign
          </div>
          <Link to="/dashboard" className="text-[12px] text-ink-muted hover:text-ink inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to dashboard
          </Link>
        </div>
      </div>

      <CompetitionHeader />
      <CompTitleBlock />
      <StatGrid />
      <ScoreTabs active={tab} onChange={setTab} />

      {tab === "best" && <EclecticScoreTab />}
      {tab === "rounds" && <AllRoundsTab />}
      {tab === "leaderboard" && <PlaceholderTab icon={Award} title="Leaderboard" />}
      {tab === "social" && <PlaceholderTab icon={Users} title="Social" />}

      <StickyEnterScoreCTA />
    </div>
  );
}
